import type { App } from 'vue'

interface PreservedElement {
  element: HTMLElement
  placeholderId: string
}

/**
 * Temporarily removes elements matching the given CSS selectors from the DOM,
 * replacing each with a uniquely identified placeholder `<div>`.
 *
 * Returns an array of preserved references used by `restoreElements()`.
 */
function removeElements(selectors: string[]): PreservedElement[] {
  const preserved: PreservedElement[] = []

  selectors.forEach((selector, index) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element, matchIndex) => {
      const placeholderId = `vue-preserve-${index}-${matchIndex}`
      const placeholder = document.createElement('div')
      placeholder.id = placeholderId

      element.replaceWith(placeholder)
      document.body.appendChild(element)
      element.style.display = 'none'

      preserved.push({ element, placeholderId })
    })
  })

  return preserved
}

/**
 * Restores previously removed elements back into the DOM,
 * replacing their placeholders.
 */
function restoreElements(preserved: PreservedElement[]): void {
  for (const { element, placeholderId } of preserved) {
    const placeholder = document.getElementById(placeholderId)
    if (placeholder) {
      element.style.display = ''
      placeholder.replaceWith(element)
    }
  }
}

/**
 * Mount a Vue app while excluding matched elements from template compilation.
 *
 * Accepts any valid CSS selector — IDs, classes, attribute selectors, etc.
 * Elements matching `exclude` are moved out of the DOM before Vue compiles
 * the in-DOM template, then restored to their original positions after mount.
 *
 * @example
 *   safeMountVue(app, '#vue-app', [
 *     '#klarna-checkout-container',
 *     '.third-party-widget',
 *     '[data-no-vue]',
 *   ])
 */
export function safeMountVue(app: App, rootSelector: string, exclude: string[]): void {
  const preserved = removeElements(exclude)

  app.mount(rootSelector)

  restoreElements(preserved)
}
